import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Upload, Download, FileSpreadsheet, Clock, Trash2, AlertCircle,
  CheckCircle, ChevronDown, ChevronUp, Info, Database, Lock,
  ArrowRight, Loader2, Search, Filter, XCircle, ShieldAlert,
  BookOpen, RefreshCw, AlertTriangle, Settings, ClipboardList, Sparkles, Layers
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { MetricHeroStats } from '../components/layout/MetricHeroStats';
import { getLightSectionTheme } from '../lib/sectionThemes';
import CapabilityInterventionMapEditor from '../components/data-hub/CapabilityInterventionMapEditor';
import CapabilityDataImporter from '../components/data-hub/CapabilityDataImporter';
import LeiRawCapturePanel from '../components/data-hub/LeiRawCapturePanel';

// Status color mapping
const STATUS_COLORS = {
  active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  coming_soon: 'bg-slate-100 text-slate-500 border-slate-200',
};

// Category groupings for data types
const CATEGORIES = [
  {
    id: 'lei',
    label: 'LEI (Learning Experience & Investment)',
    color: 'teal',
    types: ['lei_averaged', 'lei_raw'],
  },
  {
    id: 'pe',
    label: 'PE (People Engine)',
    color: 'indigo',
    types: ['pe_capability', 'pe_retention'],
  },
  {
    id: 'roe',
    label: 'ROE (Return on Expectation)',
    color: 'amber',
    types: ['roe_motivation', 'roe_l2', 'roe_l3'],
  },
  {
    id: 'kpi',
    label: 'KPI (Business Outcomes)',
    color: 'rose',
    types: ['kpi_actuals'],
  },
  {
    id: 'future',
    label: 'Raw Scores (Coming Soon)',
    color: 'slate',
    types: ['capability_raw', 'motivation_raw', 'l2_raw', 'l3_raw'],
  },
];

function DataTypeCard({ dtype, onDownloadTemplate, onImport, isImporting, isHighlighted, isSuperAdmin, onCadenceChange, onOpenGuided }) {
  const isActive = dtype.status === 'active';
  const fileRef = useRef(null);

  return (
    <div
      className={`rounded-xl border p-5 transition-all ${
        isActive
          ? 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
          : 'bg-slate-50/60 border-dashed border-slate-200 opacity-60'
      } ${
        isHighlighted
          ? 'ring-2 ring-amber-400 ring-offset-2 shadow-md animate-pulse-slow'
          : ''
      }`}
      data-testid={`dtype-card-${dtype.id}`}
      data-stale-highlight={isHighlighted ? 'true' : undefined}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-semibold text-slate-900 text-sm truncate">
              {dtype.name}
            </h4>
            {!isActive && <Lock className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />}
          </div>
          <p className="text-xs text-slate-500 line-clamp-2">{dtype.description}</p>
        </div>
        <Badge
          variant="outline"
          className={`ml-3 flex-shrink-0 text-[10px] ${STATUS_COLORS[dtype.status]}`}
        >
          {isActive ? 'Active' : 'Coming Soon'}
        </Badge>
      </div>

      {isActive && (
        <>
          <div className="flex items-center gap-4 text-xs text-slate-500 mb-3">
            <span className="flex items-center gap-1">
              <Database className="h-3 w-3" />
              {dtype.record_count.toLocaleString()} records
            </span>
            {dtype.last_import && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {new Date(dtype.last_import).toLocaleDateString()}
              </span>
            )}
          </div>

          {dtype.feeds && dtype.feeds.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {dtype.feeds.map((feed) => (
                <span
                  key={feed}
                  className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded"
                >
                  {feed}
                </span>
              ))}
            </div>
          )}

          {isSuperAdmin && (
            <div
              className="flex items-center gap-2 mb-3 pt-2 border-t border-dashed border-slate-200"
              data-testid={`dtype-cadence-row-${dtype.id}`}
            >
              <Settings className="h-3 w-3 text-slate-400" />
              <label
                htmlFor={`cadence-${dtype.id}`}
                className="text-[11px] font-medium text-slate-500"
              >
                Refresh cadence
              </label>
              <select
                id={`cadence-${dtype.id}`}
                value={dtype.stale_threshold_days ?? 60}
                onChange={(e) => onCadenceChange(dtype.id, parseInt(e.target.value, 10))}
                className="text-[11px] rounded border border-slate-200 bg-white px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-teal-500"
                data-testid={`cadence-select-${dtype.id}`}
              >
                {[30, 60, 90, 120, 180, 365].map((d) => (
                  <option key={d} value={d}>{d} days</option>
                ))}
              </select>
              {dtype.stale_threshold_is_override && (
                <button
                  type="button"
                  onClick={() => onCadenceChange(dtype.id, null)}
                  className="text-[10px] text-slate-500 hover:text-slate-800 underline"
                  title={`Reset to system default (${dtype.stale_threshold_default}d)`}
                  data-testid={`cadence-reset-${dtype.id}`}
                >
                  Reset
                </button>
              )}
              {dtype.stale_threshold_is_override && (
                <span
                  className="text-[10px] text-teal-700 bg-teal-50 border border-teal-200 rounded px-1 py-0.5"
                  title={`System default is ${dtype.stale_threshold_default}d`}
                >
                  Override
                </span>
              )}
            </div>
          )}

          <div className="flex gap-2 mt-auto">
            <Button
              variant="outline"
              size="sm"
              className="text-xs h-8"
              onClick={() => onDownloadTemplate(dtype.id)}
              data-testid={`download-tpl-${dtype.id}`}
            >
              <Download className="h-3 w-3 mr-1" />
              Template
            </Button>
            <input
              type="file"
              ref={fileRef}
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.[0]) {
                  onImport(dtype.id, e.target.files[0]);
                  fileRef.current.value = '';
                }
              }}
              data-testid={`file-input-${dtype.id}`}
            />
            <Button
              size="sm"
              className="text-xs h-8 bg-teal-600 hover:bg-teal-700"
              disabled={isImporting}
              onClick={() => fileRef.current?.click()}
              data-testid={`import-btn-${dtype.id}`}
            >
              {isImporting ? (
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Upload className="h-3 w-3 mr-1" />
              )}
              Import CSV
            </Button>
          </div>
          {dtype.id === 'lei_raw' && onOpenGuided && (
            <button
              type="button"
              onClick={onOpenGuided}
              className="mt-2 w-full text-left text-[11px] text-cyan-700 bg-cyan-50 border border-cyan-100 rounded px-2 py-1.5 hover:bg-cyan-100 transition-colors"
              data-testid="lei-raw-guided-link"
            >
              💡 Same data, two doors — for a guided experience with a <strong>field guide</strong> and <strong>live coverage</strong>, use the <strong>LEI Raw tab →</strong>
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ImportResultBanner({ result, onDismiss }) {
  if (!result) return null;
  const isSuccess = result.status === 'success';
  const isPartial = result.status === 'partial';

  return (
    <Alert
      className={`${
        isSuccess
          ? 'border-emerald-300 bg-emerald-50'
          : isPartial
          ? 'border-amber-300 bg-amber-50'
          : 'border-rose-300 bg-rose-50'
      }`}
      data-testid="import-result-banner"
    >
      {isSuccess ? (
        <CheckCircle className="h-4 w-4 text-emerald-600" />
      ) : (
        <AlertCircle className="h-4 w-4 text-rose-600" />
      )}
      <AlertDescription>
        <div className="flex items-start justify-between">
          <div>
            <p
              className={`font-medium text-sm ${
                isSuccess
                  ? 'text-emerald-800'
                  : isPartial
                  ? 'text-amber-800'
                  : 'text-rose-800'
              }`}
            >
              {isSuccess
                ? (result.data_type === 'lei_raw'
                    ? `Ingested ${result.rows_ingested} components → recomputed ${result.measures_recomputed} measures${result.avg_confidence != null ? ` · avg confidence ${Math.round(result.avg_confidence * 100)}%` : ''}`
                    : `Successfully imported ${result.imported} records`)
                : isPartial
                ? `Partially imported: ${result.imported} ok, ${result.failed} failed`
                : `Import failed: ${result.failed} rows had errors`}
            </p>
            {result.warnings && result.warnings.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="import-warnings">
                <p className="text-xs font-medium text-amber-700">Data warnings ({result.warnings.length}):</p>
                {result.warnings.slice(0, 6).map((w, i) => (
                  <p key={`warn-${i}`} className="text-xs text-amber-700">
                    {w.measure_id} ({w.period}): {w.warning}
                  </p>
                ))}
                {result.warnings.length > 6 && (
                  <p className="text-xs text-amber-600 italic">...and {result.warnings.length - 6} more</p>
                )}
              </div>
            )}
            {result.errors && result.errors.length > 0 && (
              <div className="mt-2 space-y-1">
                <p className="text-xs font-medium text-rose-700">Error details:</p>
                {result.errors.slice(0, 5).map((err, i) => (
                  <p key={`err-${err.row}-${i}`} className="text-xs text-rose-600">
                    Row {err.row}: {err.errors?.join(', ')}
                  </p>
                ))}
                {result.errors.length > 5 && (
                  <p className="text-xs text-rose-500 italic">
                    ...and {result.errors.length - 5} more
                  </p>
                )}
              </div>
            )}
          </div>
          <button onClick={onDismiss} className="text-slate-400 hover:text-slate-600">
            <XCircle className="h-4 w-4" />
          </button>
        </div>
      </AlertDescription>
    </Alert>
  );
}

function HistoryRow({ record }) {
  const [expanded, setExpanded] = useState(false);
  const statusColor =
    record.status === 'success'
      ? 'text-emerald-700 bg-emerald-50'
      : record.status === 'partial'
      ? 'text-amber-700 bg-amber-50'
      : 'text-rose-700 bg-rose-50';

  return (
    <div
      className="border border-slate-200 rounded-lg overflow-hidden"
      data-testid={`history-row-${record.id}`}
    >
      <button
        className="w-full flex items-center justify-between p-3 hover:bg-slate-50 text-left"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Badge variant="outline" className={`text-[10px] ${statusColor}`}>
            {record.status}
          </Badge>
          <span className="text-sm font-medium text-slate-800 truncate">
            {record.data_type_name}
          </span>
          <span className="text-xs text-slate-500">{record.filename}</span>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <span className="text-xs text-slate-500">
            {record.imported}/{record.total_rows} rows
          </span>
          <span className="text-xs text-slate-400">
            {new Date(record.created_at).toLocaleDateString()}
          </span>
          {expanded ? (
            <ChevronUp className="h-4 w-4 text-slate-400" />
          ) : (
            <ChevronDown className="h-4 w-4 text-slate-400" />
          )}
        </div>
      </button>
      {expanded && record.errors && record.errors.length > 0 && (
        <div className="border-t border-slate-100 bg-slate-50 p-3 space-y-1">
          <p className="text-xs font-medium text-slate-600 mb-2">
            {record.errors.length} error(s):
          </p>
          {record.errors.slice(0, 10).map((err, i) => (
            <div key={`hist-err-${i}`} className="text-xs text-rose-600 flex gap-2">
              <span className="font-mono text-rose-500">Row {err.row}:</span>
              <span>{err.errors?.join(', ')}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DeletePanel({ organizations, onDelete, isDeleting }) {
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [confirmText, setConfirmText] = useState('');

  const activeTypes = CATEGORIES.flatMap((c) => c.types).filter(
    (t) =>
      !['capability_raw', 'motivation_raw', 'l2_raw', 'l3_raw'].includes(t)
  );

  const toggleOrg = (id) =>
    setSelectedOrgs((prev) =>
      prev.includes(id) ? prev.filter((o) => o !== id) : [...prev, id]
    );

  const toggleType = (id) =>
    setSelectedTypes((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );

  const canDelete =
    selectedOrgs.length > 0 &&
    selectedTypes.length > 0 &&
    confirmText === 'DELETE';

  return (
    <Card className="border-rose-200 bg-rose-50/30" data-testid="delete-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-rose-800 text-base">
          <ShieldAlert className="h-5 w-5" />
          Data Deletion (Super Admin)
        </CardTitle>
        <CardDescription className="text-rose-600">
          Permanently delete imported data for selected organizations and data types.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs font-medium text-slate-700 mb-2">Select Organizations:</p>
          <div className="flex flex-wrap gap-2">
            {organizations.map((org) => (
              <button
                key={org.id}
                onClick={() => toggleOrg(org.id)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  selectedOrgs.includes(org.id)
                    ? 'bg-rose-600 text-white border-rose-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-rose-300'
                }`}
                data-testid={`del-org-${org.id}`}
              >
                {org.name}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs font-medium text-slate-700 mb-2">Select Data Types:</p>
          <div className="flex flex-wrap gap-2">
            {activeTypes.map((t) => (
              <button
                key={t}
                onClick={() => toggleType(t)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                  selectedTypes.includes(t)
                    ? 'bg-rose-600 text-white border-rose-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:border-rose-300'
                }`}
                data-testid={`del-type-${t}`}
              >
                {t.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>
        <div>
          <p className="text-xs text-rose-700 mb-1">
            Type <strong>DELETE</strong> to confirm:
          </p>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className="text-sm border border-rose-300 rounded px-3 py-1.5 w-40 focus:outline-none focus:ring-2 focus:ring-rose-400"
            placeholder="DELETE"
            data-testid="delete-confirm-input"
          />
        </div>
        <Button
          variant="destructive"
          disabled={!canDelete || isDeleting}
          onClick={() =>
            onDelete(selectedOrgs, selectedTypes, () => {
              setSelectedOrgs([]);
              setSelectedTypes([]);
              setConfirmText('');
            })
          }
          className="gap-2"
          data-testid="execute-delete-btn"
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Trash2 className="h-4 w-4" />
          )}
          Delete Selected Data
        </Button>
      </CardContent>
    </Card>
  );
}

export default function DataManagementHub() {
  const { isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [overview, setOverview] = useState([]);
  const [history, setHistory] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [loading, setLoading] = useState(true);
  const [importResult, setImportResult] = useState(null);
  const [importingType, setImportingType] = useState(null);
  const [strictWarnings, setStrictWarnings] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [historyFilter, setHistoryFilter] = useState('all');
  // Short-lived Set of dtype ids to highlight with an amber pulse on
  // the Overview grid — triggered when the user clicks the hero's
  // "Stale" tile. Expires via timer so the grid returns to its
  // normal look after ~3s.
  const [highlightedIds, setHighlightedIds] = useState(null);

  const fetchOverview = useCallback(async (orgId) => {
    try {
      const params = orgId ? `?organization_id=${orgId}` : '';
      const res = await api.get(`/data-management/overview${params}`);
      setOverview(res.data.data_types || []);
      if (!orgId && res.data.organization_id) {
        setSelectedOrg(res.data.organization_id);
      }
    } catch (e) {
      logger.error('Failed to fetch overview:', e);
    }
  }, []);

  const fetchHistory = useCallback(async (orgId) => {
    try {
      const params = orgId ? `?organization_id=${orgId}` : '';
      const res = await api.get(`/data-management/history${params}`);
      setHistory(res.data.history || []);
    } catch (e) {
      logger.error('Failed to fetch history:', e);
    }
  }, []);

  const fetchOrgs = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await api.get('/rbac/organizations');
      setOrganizations(res.data.organizations || []);
    } catch (e) {
      logger.error('Error fetching orgs:', e);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchOverview(), fetchHistory(), fetchOrgs()]);
      setLoading(false);
    };
    init();
  }, [fetchOverview, fetchHistory, fetchOrgs]);

  // Re-fetch when org changes
  useEffect(() => {
    if (selectedOrg) {
      fetchOverview(selectedOrg);
      fetchHistory(selectedOrg);
    }
  }, [selectedOrg, fetchOverview, fetchHistory]);

  const handleCadenceChange = async (dataType, days) => {
    try {
      await api.patch(`/data-management/types/${dataType}/config`, {
        stale_threshold_days: days,
      });
      toast.success(
        days === null
          ? 'Reset to system default'
          : `Refresh cadence set to ${days} days`,
      );
      if (selectedOrg) fetchOverview(selectedOrg);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update cadence');
      logger.error('Cadence update failed:', e);
    }
  };

  const handleDownloadTemplate = async (dataType) => {
    try {
      const res = await api.get(`/data-management/templates/${dataType}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataType}_template.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Template for ${dataType} downloaded`);
    } catch (e) {
      toast.error('Failed to download template');
      logger.error('Template download error:', e);
    }
  };

  const handleImport = async (dataType, file) => {
    setImportingType(dataType);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const qp = [];
      if (selectedOrg) qp.push(`organization_id=${selectedOrg}`);
      if (dataType === 'lei_raw' && strictWarnings) qp.push('strict=true');
      const params = qp.length ? `?${qp.join('&')}` : '';
      const res = await api.post(
        `/data-management/import/${dataType}${params}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setImportResult(res.data);
      const r = res.data;
      if (dataType === 'lei_raw') {
        const conf = r.avg_confidence != null ? ` · avg confidence ${Math.round(r.avg_confidence * 100)}%` : '';
        const msg = `Ingested ${r.rows_ingested} components → recomputed ${r.measures_recomputed} measures${conf}`;
        if (r.rejected_groups > 0) {
          toast.warning(`${msg}. ${r.rejected_groups} measure/period group(s) rejected for data warnings (strict mode).`);
        } else if (r.warnings && r.warnings.length) {
          toast.warning(`${msg}. ${r.warnings.length} data warning(s) — review below.`);
        } else if (r.status === 'partial') {
          toast.warning(`${msg}. ${r.failed} row(s) failed.`);
        } else if (r.status === 'failed') {
          toast.error(`Import failed: ${r.failed} rows had errors`);
        } else {
          toast.success(msg);
        }
      } else if (r.status === 'success') {
        toast.success(`Imported ${r.imported} records`);
      } else if (r.status === 'partial') {
        toast.warning(`Imported ${r.imported}, ${r.failed} failed`);
      } else {
        toast.error(`Import failed: ${r.failed} rows had errors`);
      }
      fetchOverview(selectedOrg);
      fetchHistory(selectedOrg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Import failed');
      logger.error('Import error:', e);
    } finally {
      setImportingType(null);
    }
  };

  const handleDelete = async (orgIds, dataTypes, resetFn) => {
    setIsDeleting(true);
    try {
      const res = await api.post('/data-management/delete', {
        organization_ids: orgIds,
        data_types: dataTypes,
      });
      toast.success(`Deleted ${res.data.total_deleted} records`);
      resetFn();
      fetchOverview(selectedOrg);
      fetchHistory(selectedOrg);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const filteredHistory =
    historyFilter === 'all'
      ? history
      : history.filter((h) => h.data_type === historyFilter);

  // Overview stats
  const totalRecords = overview
    .filter((d) => d.status === 'active')
    .reduce((sum, d) => sum + d.record_count, 0);
  const activeTypes = overview.filter((d) => d.status === 'active').length;
  const typesWithData = overview.filter(
    (d) => d.status === 'active' && d.record_count > 0
  ).length;

  // Stale or missing = active data types with no imports yet OR a
  // last_import older than the per-type `stale_threshold_days`
  // (defaults to 60; quarterly/semi-annual feeds override the
  // threshold on the backend DATA_TYPES config so they don't false-
  // alarm).
  const DAY_MS = 24 * 60 * 60 * 1000;
  const now = Date.now();
  const staleTypes = overview.filter((d) => {
    if (d.status !== 'active') return false;
    if (!d.record_count) return true;
    const thresholdMs = (d.stale_threshold_days ?? 60) * DAY_MS;
    const ts = d.last_import ? new Date(d.last_import).getTime() : 0;
    return ts === 0 || now - ts > thresholdMs;
  });
  const staleIds = new Set(staleTypes.map((d) => d.id));

  // Click handler for the "Stale" hero tile — pops the user onto the
  // Overview tab, flashes amber rings on the affected cards, and
  // scrolls the first one into view.
  const handleHighlightStale = () => {
    if (staleTypes.length === 0) return;
    setActiveTab('overview');
    setHighlightedIds(staleIds);
    setTimeout(() => {
      const first = document.querySelector(`[data-testid="dtype-card-${staleTypes[0].id}"]`);
      if (first) first.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    setTimeout(() => setHighlightedIds(null), 3200);
  };

  if (loading) {
    const __pageThemeLoading = getLightSectionTheme('Navy');
    return (
      <PageShell theme={__pageThemeLoading} hero={(
        <PageHero
          title="Data Management Hub"
          description="Central hub for importing, reviewing, and managing all data that feeds Seertech. Download templates, import CSVs, review history, and manage data lifecycle."
          icon={Database}
          theme={__pageThemeLoading}
          testId="data-hub-hero"
        />
      )}>
      <div className="min-h-[40vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
      </PageShell>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');

  // Hero stats mirror the old body header card.
  const heroStats = (
    <MetricHeroStats
      testId="data-hub-hero-stats"
      className="lg:grid-cols-5"
      tiles={[
        { key: 'total', label: 'Total Records', value: totalRecords.toLocaleString(), hint: 'Across all data types', testId: 'data-hub-stat-total', headline: true },
        { key: 'active', label: 'Active Data Types', value: activeTypes, hint: 'Currently wired up', testId: 'data-hub-stat-active' },
        { key: 'with_data', label: 'Types with Data', value: `${typesWithData}/${activeTypes}`, hint: 'At least one record ingested', testId: 'data-hub-stat-with-data' },
        { key: 'batches', label: 'Import Batches', value: history.length, hint: 'Lifetime uploads', testId: 'data-hub-stat-batches' },
        {
          key: 'stale',
          label: 'Stale or Missing',
          value: staleTypes.length,
          hint: staleTypes.length > 0
            ? (
              <span className="flex flex-col gap-1">
                <span className="inline-flex items-center gap-1">
                  <AlertTriangle className="h-2.5 w-2.5" />
                  Review types past their refresh cadence
                </span>
                <span className="flex flex-wrap gap-1 mt-0.5">
                  {staleTypes.slice(0, 3).map((t) => (
                    <span
                      key={t.id}
                      className="inline-block text-[9px] font-medium bg-amber-900/30 border border-amber-300/40 rounded px-1.5 py-0.5 text-amber-100"
                      title={t.name}
                    >
                      {t.id}
                    </span>
                  ))}
                  {staleTypes.length > 3 && (
                    <span className="inline-block text-[9px] font-medium text-amber-100/80 px-1 py-0.5">
                      +{staleTypes.length - 3} more
                    </span>
                  )}
                </span>
              </span>
            )
            : 'All types inside their refresh cadence',
          hintTone: staleTypes.length > 0 ? 'cta' : 'default',
          onClick: staleTypes.length > 0 ? handleHighlightStale : undefined,
          onClickTitle: staleTypes.length > 0 ? 'Click to spotlight stale or missing data types' : undefined,
          testId: 'data-hub-stat-stale',
        },
      ]}
    />
  );

  const heroActions = (isSuperAdmin && organizations.length > 0) ? (
    <select
      value={selectedOrg}
      onChange={(e) => setSelectedOrg(e.target.value)}
      className="text-sm rounded-lg px-3 py-2 bg-white border border-slate-200 text-slate-900 focus:ring-2 focus:ring-white/40 min-w-[200px] [&>option]:text-slate-900 [&>option]:bg-white"
      data-testid="hub-org-filter"
    >
      <option value="">My Organization</option>
      {organizations.map((o) => (
        <option key={o.id} value={o.id}>{o.name}</option>
      ))}
    </select>
  ) : null;

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Data Management Hub"
        description="Central hub for importing, reviewing, and managing all data that feeds Seertech. Download templates, import CSVs, review history, and manage data lifecycle."
        icon={Database}
        theme={__pageTheme}
        actions={heroActions}
        stats={heroStats}
        testId="data-hub-hero"
      />
    )}>
    <div className="space-y-6" data-testid="data-management-hub">
      <div className="space-y-6">
        {/* Import Result */}
        <ImportResultBanner
          result={importResult}
          onDismiss={() => setImportResult(null)}
        />

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-7 h-auto p-1 bg-slate-100">
            <TabsTrigger value="overview" className="gap-2 py-2.5 text-xs sm:text-sm">
              <Database className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="import" className="gap-2 py-2.5 text-xs sm:text-sm">
              <Upload className="h-4 w-4" />
              Import
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2 py-2.5 text-xs sm:text-sm">
              <Clock className="h-4 w-4" />
              History
            </TabsTrigger>
            <TabsTrigger value="capability_data" className="gap-2 py-2.5 text-xs sm:text-sm" data-testid="tab-capability-data">
              <ClipboardList className="h-4 w-4" />
              Capability Data
            </TabsTrigger>
            <TabsTrigger value="skill_map" className="gap-2 py-2.5 text-xs sm:text-sm" data-testid="tab-skill-map">
              <Settings className="h-4 w-4" />
              Skill Map
            </TabsTrigger>
            <TabsTrigger value="reference" className="gap-2 py-2.5 text-xs sm:text-sm">
              <BookOpen className="h-4 w-4" />
              Reference
            </TabsTrigger>
            <TabsTrigger value="lei_raw" className="gap-2 py-2.5 text-xs sm:text-sm" data-testid="tab-lei-raw">
              <Layers className="h-4 w-4" />
              LEI Raw
            </TabsTrigger>
          </TabsList>

          {/* ────── OVERVIEW TAB ────── */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <a href="/benchmark-baseline" className="block group" data-testid="benchmark-baseline-cta">
              <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50/70 to-white hover:shadow-md transition-shadow">
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="h-11 w-11 rounded-xl bg-cyan-100 flex items-center justify-center shrink-0">
                    <Sparkles className="h-5 w-5 text-cyan-700" />
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-slate-800 text-sm">No data yet? Start with a Benchmark Baseline</div>
                    <div className="text-xs text-slate-500">Synthesize a defensible modeled dataset from industry × size benchmarks so the whole value chain runs — then replace it with your own data over time.</div>
                  </div>
                  <ArrowRight className="h-5 w-5 text-cyan-600 group-hover:translate-x-0.5 transition-transform" />
                </CardContent>
              </Card>
            </a>
            {CATEGORIES.map((cat) => {
              const types = overview.filter((d) => cat.types.includes(d.id));
              if (types.length === 0) return null;
              const isFuture = cat.id === 'future';
              return (
                <div key={cat.id}>
                  <h3
                    className={`text-sm font-semibold mb-3 ${
                      isFuture ? 'text-slate-400' : 'text-slate-700'
                    }`}
                  >
                    {cat.label}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {types.map((dt) => (
                      <DataTypeCard
                        key={dt.id}
                        dtype={dt}
                        onDownloadTemplate={handleDownloadTemplate}
                        onImport={handleImport}
                        isImporting={importingType === dt.id}
                        isHighlighted={highlightedIds?.has(dt.id)}
                        isSuperAdmin={isSuperAdmin}
                        onCadenceChange={handleCadenceChange}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* ────── IMPORT TAB ────── */}
          <TabsContent value="import" className="space-y-6 mt-6">
            <Card className="border-teal-200 bg-gradient-to-br from-teal-50/50 to-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-teal-800 text-base">
                  <Info className="h-5 w-5" />
                  How to Import Data
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
                  <li>
                    Select a data type below and click <strong>Download Template</strong> to
                    get the CSV template with required columns and sample rows.
                  </li>
                  <li>
                    Fill in your data following the template format. Required columns must
                    have values; optional columns can be left blank.
                  </li>
                  <li>
                    Click <strong>Import CSV</strong> and select your completed file. The
                    system validates each row and reports any errors.
                  </li>
                  <li>
                    Review the import result. Successfully imported rows appear immediately
                    in the relevant dashboards.
                  </li>
                </ol>
                <label className="mt-3 flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="lei-strict-toggle">
                  <input type="checkbox" checked={strictWarnings} onChange={(e) => setStrictWarnings(e.target.checked)} className="rounded border-slate-300" />
                  <span><strong>Reject rows with data warnings</strong> for LEI Raw Components (e.g., participants &gt; eligible). Off = warn but still import.</span>
                </label>
              </CardContent>
            </Card>

            {CATEGORIES.filter((c) => c.id !== 'future').map((cat) => {
              const types = overview.filter(
                (d) => cat.types.includes(d.id) && d.status === 'active'
              );
              if (types.length === 0) return null;
              return (
                <div key={cat.id}>
                  <h3 className="text-sm font-semibold text-slate-700 mb-3">
                    {cat.label}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {types.map((dt) => (
                      <DataTypeCard
                        key={dt.id}
                        dtype={dt}
                        onDownloadTemplate={handleDownloadTemplate}
                        onImport={handleImport}
                        isImporting={importingType === dt.id}
                        onOpenGuided={() => setActiveTab('lei_raw')}
                      />
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Greyed-out future types */}
            <div>
              <h3 className="text-sm font-semibold text-slate-400 mb-3">
                Raw Scores (Coming Soon)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {overview
                  .filter((d) => d.status === 'coming_soon')
                  .map((dt) => (
                    <DataTypeCard
                      key={dt.id}
                      dtype={dt}
                      onDownloadTemplate={() => {}}
                      onImport={() => {}}
                      isImporting={false}
                    />
                  ))}
              </div>
            </div>
          </TabsContent>

          {/* ────── HISTORY TAB ────── */}
          <TabsContent value="history" className="space-y-4 mt-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Import History</h3>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <select
                  value={historyFilter}
                  onChange={(e) => setHistoryFilter(e.target.value)}
                  className="text-xs border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-teal-400"
                  data-testid="history-type-filter"
                >
                  <option value="all">All Types</option>
                  {overview
                    .filter((d) => d.status === 'active')
                    .map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                </select>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => fetchHistory(selectedOrg)}
                  className="h-7"
                  data-testid="refresh-history-btn"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {filteredHistory.length > 0 ? (
              <div className="space-y-2">
                {filteredHistory.map((record) => (
                  <HistoryRow key={record.id} record={record} />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12 text-center">
                  <FileSpreadsheet className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">
                    No import history yet. Import data from the Import tab to see records
                    here.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Super Admin Delete Panel */}
            {isSuperAdmin && (
              <DeletePanel
                organizations={organizations}
                onDelete={handleDelete}
                isDeleting={isDeleting}
              />
            )}
          </TabsContent>

          {/* ────── CAPABILITY DATA TAB ────── */}
          <TabsContent value="capability_data" className="space-y-4 mt-6">
            <CapabilityDataImporter selectedOrg={selectedOrg} />
          </TabsContent>

          {/* ────── SKILL MAP TAB ────── */}
          <TabsContent value="skill_map" className="space-y-4 mt-6">
            <CapabilityInterventionMapEditor selectedOrg={selectedOrg} />
          </TabsContent>

          {/* ────── REFERENCE TAB ────── */}
          <TabsContent value="reference" className="space-y-4 mt-6">
            <ReferenceTab />
          </TabsContent>
          <TabsContent value="lei_raw" className="space-y-4 mt-6">
            <LeiRawCapturePanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
    </PageShell>
  );
}

function ReferenceTab() {
  const [dictionary, setDictionary] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/data-management/dictionary');
        setDictionary(res.data.data_types || {});
      } catch (e) {
        logger.error('Failed to fetch dictionary:', e);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-slate-600" />
            Data Dictionary & ETL Reference
          </CardTitle>
          <CardDescription>
            Complete schema reference for all data types. Use this to configure ETL
            pipelines or API integrations.
          </CardDescription>
        </CardHeader>
      </Card>

      {Object.entries(dictionary).map(([id, dt]) => (
        <Card
          key={id}
          className={dt.status === 'coming_soon' ? 'opacity-50' : ''}
          data-testid={`dict-${id}`}
        >
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm">{dt.name}</CardTitle>
              <Badge
                variant="outline"
                className={`text-[10px] ${STATUS_COLORS[dt.status]}`}
              >
                {dt.status === 'active' ? 'Active' : 'Coming Soon'}
              </Badge>
            </div>
            <CardDescription className="text-xs">{dt.description}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 pt-0">
            {dt.collection && (
              <div className="text-xs">
                <span className="text-slate-500">Collection:</span>{' '}
                <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                  {dt.collection}
                </code>
              </div>
            )}
            {dt.required_fields?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Required Fields:</p>
                <div className="flex flex-wrap gap-1">
                  {dt.required_fields.map((f) => (
                    <code
                      key={f}
                      className="text-[10px] bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded border border-rose-100"
                    >
                      {f}
                    </code>
                  ))}
                </div>
              </div>
            )}
            {dt.optional_fields?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Optional Fields:</p>
                <div className="flex flex-wrap gap-1">
                  {dt.optional_fields.map((f) => (
                    <code
                      key={f}
                      className="text-[10px] bg-slate-50 text-slate-600 px-1.5 py-0.5 rounded border border-slate-200"
                    >
                      {f}
                    </code>
                  ))}
                </div>
              </div>
            )}
            {dt.api_endpoint && (
              <div className="text-xs">
                <span className="text-slate-500">API:</span>{' '}
                <code className="bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded">
                  {dt.api_endpoint}
                </code>
              </div>
            )}
            {dt.feeds?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">Feeds into:</p>
                <div className="flex flex-wrap gap-1">
                  {dt.feeds.map((f) => (
                    <span
                      key={f}
                      className="text-[10px] bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded"
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
